import { useEffect, useState } from "react";
import { Search, Filter } from "lucide-react";
import { api } from "../lib/api";
import { HOME } from "../constants/testIds";
import RankingTable from "./RankingTable";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

function useDebounced(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export function ServidoresPanel({ refreshKey }) {
  const [search, setSearch] = useState("");
  const [orgao, setOrgao] = useState("all");
  const [orgaos, setOrgaos] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const q = useDebounced(search, 250);

  useEffect(() => {
    api
      .get("/orgaos")
      .then((r) => setOrgaos(r.data.orgaos || []))
      .catch(() => setOrgaos([]));
  }, [refreshKey]);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 100 };
    if (q) params.search = q;
    if (orgao && orgao !== "all") params.orgao = orgao;
    api
      .get("/servidores", { params })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [q, orgao, refreshKey]);

  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-[1fr_260px]">
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            aria-hidden="true"
          />
          <Input
            data-testid={HOME.searchInput}
            placeholder="Buscar por nome do servidor..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-11 border-slate-200 bg-white pl-9 text-sm"
          />
        </div>
        <div className="relative">
          <Filter
            className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-slate-400"
            aria-hidden="true"
          />
          <Select value={orgao} onValueChange={setOrgao}>
            <SelectTrigger
              data-testid={HOME.orgaoSelect}
              className="h-11 border-slate-200 bg-white pl-9 text-sm"
            >
              <SelectValue placeholder="Filtrar por órgão" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Todos os órgãos</SelectItem>
              {orgaos.map((o) => (
                <SelectItem key={o} value={o}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <RankingTable items={items} type="servidor" loading={loading} />
    </div>
  );
}

export function PoliticosPanel({ refreshKey, initialSearch = "" }) {
  const [tipo, setTipo] = useState("all"); // all|deputado|senador
  const [partido, setPartido] = useState("all");
  const [partidos, setPartidos] = useState([]);
  const [search, setSearch] = useState(initialSearch);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const q = useDebounced(search, 250);

  useEffect(() => {
    if (initialSearch) setSearch(initialSearch);
  }, [initialSearch]);

  useEffect(() => {
    api
      .get("/partidos")
      .then((r) => setPartidos(r.data.partidos || []))
      .catch(() => setPartidos([]));
  }, [refreshKey]);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 200 };
    if (tipo !== "all") params.tipo = tipo;
    if (partido !== "all") params.partido = partido;
    if (q) params.search = q;
    api
      .get("/politicos", { params })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [tipo, partido, q, refreshKey]);

  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-[1fr_180px_180px]">
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            aria-hidden="true"
          />
          <Input
            data-testid={`${HOME.searchInput}-politicos`}
            placeholder="Buscar por nome do político..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-11 border-slate-200 bg-white pl-9 text-sm"
          />
        </div>
        <Select value={tipo} onValueChange={setTipo}>
          <SelectTrigger
            className="h-11 border-slate-200 bg-white text-sm"
            data-testid="tipo-politico-select"
          >
            <SelectValue placeholder="Câmara & Senado" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="deputado">Deputados</SelectItem>
            <SelectItem value="senador">Senadores</SelectItem>
          </SelectContent>
        </Select>
        <Select value={partido} onValueChange={setPartido}>
          <SelectTrigger
            data-testid={HOME.partidoSelect}
            className="h-11 border-slate-200 bg-white text-sm"
          >
            <SelectValue placeholder="Partido" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">Todos os partidos</SelectItem>
            {partidos.map((p) => (
              <SelectItem key={p} value={p}>
                {p}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <RankingTable items={items} type="politico" loading={loading} />
    </div>
  );
}
